import { MarketplaceListingRouter, NewMarketplaceListingContainer } from '../marketplace-listings'
import React, { useEffect } from 'react'
import { Router as ReactRouter, Redirect, Route, Switch, useLocation } from 'react-router'

import { AccountDetailPage } from '../account-detail-page'
import { ActivationCodeRouter } from '../activation-code'
import { AIToolingSessionSummary } from '../ai-tooling-session-summary'
import { AIToolingSessionsList } from '../ai-tooling-sessions-list'
import { BoardRouter } from '../board'
import { ChannelDetail } from '../channel-detail'
import { ChannelsReport } from '../channels-report'
import { ColorTemplateRouterV2 } from '../color-template-v2'
import { DashboardRouter } from '../dashboard'
import { DealAssociationsRouter } from '../deal-associations'
import { DevelopersRouter } from '../developers'
import { DeviceBatchView } from '../device-batch-view/DeviceBatchView'
import { DeviceListPage } from '../device-list-page'
import { DevicePage } from '../device-page'
import { DigitalDevicesRouter } from '../digital-devices'
import { FeedItemRouterV2 } from '../feed-items-v2'
import { HardwareListPage } from '../hardware-list-page'
import { HardwarePage } from '../hardware-page'
import { HawkbitManager } from '../hawkbit-manager'

import { HomeScreenBannerRouter } from '../home-screen-banner'
import { InstallablesRouter } from '../installables'
import { Installation } from '../installation'
import { ListPlusRenewalFailures } from '../list-plus-renewal-failures'
import { MarketplaceGallery } from '../marketplace-gallery'
import { MarketplaceListingFeatureTypeList } from '../marketplace-listing-feature-type-list'
import { MessageAppearanceRouterV2 } from '../message-appearance-v2'
import { MessageRouterV2 } from '../message-v2'
import { MessageSetRouterV2 } from '../message-set-v2'
import { MessageTemplatesRouter } from '../message-templates'
import { NewInstallable } from '../installables'
import { NoteArrayRouter } from '../note-array'
import { NotificationsRouter } from '../notifications'
import { OrderPage } from '../order-page'
import { OrdersListPage } from '../orders-list-page'
import { OsVersion } from '../os-version'
import { OsVersionList } from '../os-version/OsVersionList'
import { PairingCodeRedirect } from '../pairing-code-redirect'
import { PaymentListPage } from '../payment-list-page'
import { PersonDetailPage } from '../person-detail-page'
import { PicksRouterV2 } from '../picks-v2'
import { PlusRouterV1 } from '../plus'
import { PromotionCodeRouter } from '../promotion-codes'
import { Principal } from '../principal'
import { QuestionsAndAnswers } from '../questions-and-answers'
import { ReferralsRouter } from '../referral'
import { ReservePairingCodes } from '../reserve-pairing-codes'
import { SearchTerms } from '../search-terms'
import { SerialExceptionChecker } from '../serial-exception-checker'
import { ServiceJwt } from '../service-jwt'
import { Dlq } from '../dlq'
import { SubmittedMessageSetRouterV2 } from '../message-set-submissions-v2'
import { MessageAppearanceActionAnalyticsPage } from '../subscription/MessageAppearanceActionAnalyticsPage'
import { Subscription } from '../subscription'
import { SuperadminListPage } from '../superadmin-list-page'
import { SyncProvisionedDevices } from '../sync-provisioned-devices'
import { Tenant } from '../tenant'
import { TenantList } from '../tenant-list'
import { UserListPage } from '../user-list-page'
import { BatchList, BatchDetail } from '../tag-review'
import { YoctoRollout } from '../yocto-rollout'
import { createBrowserHistory } from 'history'
import { Dashboard } from '../dashboard-v2'
import { InternalReportsRouter } from '../internal-reports/Router'

type RouterProps = IRouterProps

interface IRouterProps {}

export const HISTORY = createBrowserHistory()

type RouteComponentMapping = { [key: string]: React.ComponentType }

const RouteWatcher = () => {
  const location = useLocation()

  useEffect(() => {
    if (location.pathname !== '/') {
      localStorage.setItem('currentPage', location.pathname)
    }
  }, [location])

  return null
}

export const Router: React.FC<RouterProps> = props => {
  const detailPages: RouteComponentMapping = {
    devices: DevicePage,
    hardware: HardwarePage,
    person: PersonDetailPage,
    channel: ChannelDetail
  }

  const indexPages: RouteComponentMapping = {
    accounts: UserListPage,
    devices: DeviceListPage,
    tenants: TenantList,
    superadmins: SuperadminListPage,
    orders: OrdersListPage,
    payments: PaymentListPage,
    hardwares: HardwareListPage,
    users: UserListPage
  }

  const recordDetailRoutes = Object.keys(detailPages).map(model => (
    <Route key={model} path={`/${model}/:id`} component={detailPages[model]} />
  ))

  const recordIndexRoutes = Object.keys(indexPages).map(model => (
    <Route exact key={model} path={`/${model}`} component={indexPages[model]} />
  ))

  return (
    <ReactRouter history={HISTORY}>
      <RouteWatcher />
      <Switch>
        <Route exact path={`/pairing-code/:code`} component={PairingCodeRedirect} />
        <Route exact path={`/ai-tooling/sessions`} component={AIToolingSessionsList} />
        <Route exact path={`/ai-tooling/sessions/:sessionId/summary`} component={AIToolingSessionSummary} />
        <Route exact path={`/devices/yocto-status/:status?`} component={DeviceListPage} />
        {recordDetailRoutes}
        {recordIndexRoutes}
        <Route exact path={`/device-batch`} component={DeviceBatchView} />
        <Route exact path={`/account/:emailAddress`} component={AccountDetailPage} />
        <Route exact path={`/new-installable`} component={NewInstallable} />
        <Route exact path={`/new-marketplace-listing`} component={NewMarketplaceListingContainer} />
        <Route exact path={`/yocto-rollout`} component={YoctoRollout} />
        <Route exact path={`/marketplace`} component={MarketplaceGallery} />
        <Route exact path={`/report/channels`} component={ChannelsReport} />
        <Route exact path={`/os-version/:id`} component={OsVersion} />
        <Route exact path={`/os-versions`} component={OsVersionList} />
        <Route exact path={`/orders/:id`} component={OrderPage} />
        <Route exact path={`/plus-renewal-failures`} component={ListPlusRenewalFailures} />
        <Route exact path={'/marketplace-listing-feature-type/:id'} component={MarketplaceListingFeatureTypeList} />
        <Route
          exact
          path={`/subscription/:id/message-appearance-action-analytics`}
          component={MessageAppearanceActionAnalyticsPage}
        />
        <Route exact path={`/subscription/:id`} component={Subscription} />
        <Route exact path={`/installation/:id`} component={Installation} />
        <Route exact path={`/tenant/:id`} component={Tenant} />
        <Route exact path={`/principal/:id`} component={Principal} />
        <Route exact path={`/hawkbit`} component={HawkbitManager} />
        <Route exact path={`/reserve-pairing-codes`} component={ReservePairingCodes} />
        <Route exact path={`/sync-provisioned-devices`} component={SyncProvisionedDevices} />
        <Route exact path={`/serial-exception-checker`} component={SerialExceptionChecker} />
        <Route exact path={`/service-jwt`} component={ServiceJwt} />
        <Route exact path={`/dlq`} component={Dlq} />
        <Route path={`/search-terms`} component={SearchTerms} />
        <Route path={`/questions-and-answers`} component={QuestionsAndAnswers} />
        <Route path={`/developers`} component={DevelopersRouter} />
        <Route path={'/installables'} component={InstallablesRouter} />
        <Route path={'/digital-devices'} component={DigitalDevicesRouter} />
        <Route path={`/referrals`} component={ReferralsRouter} />
        <Route path={'/marketplace-listings'} component={MarketplaceListingRouter} />
        <Route path='/deal-associations' component={DealAssociationsRouter} />
        <Route path='/color-templates' component={ColorTemplateRouterV2} />
        <Route path='/picks' component={PicksRouterV2} />
        <Route path='/message-sets' component={MessageSetRouterV2} />
        <Route path='/message-templates' component={MessageTemplatesRouter} />
        <Route path='/message' component={MessageRouterV2} />
        <Route path={'/plus'} component={PlusRouterV1} />
        <Route path='/promotion-codes' component={PromotionCodeRouter} />
        <Route path={'/home-screen-banner'} component={HomeScreenBannerRouter} />
        <Route path='/notifications' component={NotificationsRouter} />
        <Route path='/activation-codes' component={ActivationCodeRouter} />
        <Route path='/submitted-message-sets' component={SubmittedMessageSetRouterV2} />
        <Route path='/boards' component={BoardRouter} />
        <Route path='/note-arrays' component={NoteArrayRouter} />
        <Route path='/message-appearance' component={MessageAppearanceRouterV2} />
        <Route path='/feed-items' component={FeedItemRouterV2} />
        <Route exact path='/tag-review/:batchId' component={BatchDetail} />
        <Route exact path='/tag-review' component={BatchList} />
        <Route path={`/internal-reports`} component={InternalReportsRouter} />
        <Route path={`/dashboard`} component={Dashboard} />
        <Route path={`/dashboard-v1`} component={DashboardRouter} />
        <Route
          render={() => {
            return <Redirect to={`/dashboard`} />
          }}
        />
      </Switch>
    </ReactRouter>
  )
}
